import React, { useMemo, useState } from 'react';
import './usertools-css/OneRepMaxCalculator.css';

const DEFAULT_CONFIG = Object.freeze({
  kilogramsPerPound: 0.45359237,
  maximumLiftKilograms: 600,
  maximumReps: 15,
  minimumAge: 13,
  maximumAge: 100,
  maximumBodyWeightKilograms: 600,
  effort: { minimumRpe: 6, maximumRpe: 10, rpeStep: 0.5, maximumReserve: 4 },
  historyLimit: 5,
  defaultUnit: 'kg',
  defaultTargetPercentage: 80,
  promotion: {
    heading: 'Unlock Smarter Eating:',
    description: 'Get a complete breakdown of vitamins, minerals, and fats plus a personalized weight-loss plan or nutrition tailored to your training with a single',
    linkText: 'AI scan',
    href: 'https://purehealthnexus.com/',
  },
  lifts: [
    { id: 'squat', label: 'Back squat' },
    { id: 'bench', label: 'Bench press' },
    { id: 'deadlift', label: 'Deadlift' },
    { id: 'press', label: 'Overhead press' },
  ],
  formulas: [
    { id: 'epley', label: 'Epley', factor: repetitions => 1 + repetitions / 30 },
    { id: 'brzycki', label: 'Brzycki', factor: repetitions => 36 / (37 - repetitions) },
    { id: 'lombardi', label: 'Lombardi', factor: repetitions => repetitions ** 0.1 },
    { id: 'lander', label: 'Lander', factor: repetitions => 100 / (101.3 - 2.67123 * repetitions) },
  ],
  percentage: { minimum: 50, maximum: 100, step: 5 },
  repMaxMaximum: 10,
  trainingZones: [
    { through: 60, name: 'Power & speed', className: 'power', repetitions: '3-5' },
    { through: 80, name: 'Muscle growth', className: 'growth', repetitions: '6-12' },
    { through: 90, name: 'Max strength', className: 'strength', repetitions: '3-5' },
    { through: 95, name: 'Max strength', className: 'strength', repetitions: '1-3' },
    { through: 100, name: 'Max test', className: 'test', repetitions: '1' },
  ],
  warmup: [
    { fraction: 0.4, repetitions: 5 },
    { fraction: 0.6, repetitions: 3 },
    { fraction: 0.75, repetitions: 2 },
    { fraction: 0.85, repetitions: 1 },
  ],
  rounding: {
    plate: { kg: 2.5, lb: 5 },
    micro: { kg: 0.5, lb: 1 },
  },
  plates: {
    kg: { bars: [20, 15], denominations: [25, 20, 15, 10, 5, 2.5, 1.25, 0.5, 0.25], quantum: 0.25 },
    lb: { bars: [45], denominations: [45, 35, 25, 10, 5, 2.5, 1.25, 0.5], quantum: 0.25 },
  },
  plateColors: ['#f2a53a', '#67b7e7', '#f47f72', '#74caad', '#c1cbd7', '#b9a1df'],
});

const INITIAL_INPUTS = {
  lift: 'squat',
  weight: '',
  repetitions: '',
  reserve: 0,
  effortMode: 'rpe',
  formula: 'consensus',
  rounding: 'plate',
  sex: '',
  age: '',
  bodyWeight: '',
};

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const displayNumber = value => numberFormatter.format(Number(value.toFixed(2)));
const toKilograms = (value, unit, config) => unit === 'kg' ? value : value * config.kilogramsPerPound;
const fromKilograms = (value, unit, config) => unit === 'kg' ? value : value / config.kilogramsPerPound;
const weightText = (valueKilograms, unit, config) => `${displayNumber(fromKilograms(valueKilograms, unit, config))} ${unit}`;

function readNumber(value, label, minimum, maximum, optional = false) {
  if (String(value).trim() === '') {
    if (optional) return null;
    throw new Error(`${label} is required.`);
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < minimum || parsed > maximum) {
    throw new Error(`${label} must be between ${displayNumber(minimum)} and ${displayNumber(maximum)}.`);
  }
  return parsed;
}

function validateInputs(inputs, unit, config) {
  const lift = config.lifts.find(item => item.id === inputs.lift);
  if (!lift) throw new Error('Choose a valid exercise.');

  // Friendly prompt when the user hasn't entered set numbers yet
  if (!String(inputs.weight).trim() && !String(inputs.repetitions).trim()) {
    throw new Error('Enter the weight lifted and reps completed to calculate your 1RM.');
  }

  const weight = readNumber(inputs.weight, 'Weight lifted', 0.01, fromKilograms(config.maximumLiftKilograms, unit, config));
  const repetitions = readNumber(inputs.repetitions, 'Reps completed', 1, config.maximumReps);
  if (!Number.isInteger(repetitions)) throw new Error('Reps completed must be a whole number.');
  if (!Number.isFinite(inputs.reserve) || inputs.reserve < 0 || inputs.reserve > config.effort.maximumReserve || (inputs.reserve / config.effort.rpeStep) % 1 !== 0) {
    throw new Error('Choose a valid RPE or RIR value.');
  }
  if (!['rpe', 'rir'].includes(inputs.effortMode)) throw new Error('Choose a valid effort scale.');
  if (inputs.sex && !['M', 'F'].includes(inputs.sex)) throw new Error('Choose a valid sex category.');
  const age = readNumber(inputs.age, 'Age', config.minimumAge, config.maximumAge, true);
  if (age !== null && !Number.isInteger(age)) throw new Error('Age must be a whole number.');
  const bodyWeight = readNumber(inputs.bodyWeight, 'Body weight', 1, fromKilograms(config.maximumBodyWeightKilograms, unit, config), true);
  if (inputs.formula !== 'consensus' && !config.formulas.some(formula => formula.id === inputs.formula)) throw new Error('Choose a valid formula.');
  if (!['none', ...Object.keys(config.rounding)].includes(inputs.rounding)) throw new Error('Choose a valid rounding option.');
  return {
    lift, weightKilograms: toKilograms(weight, unit, config), repetitions,
    reserve: inputs.reserve, formula: inputs.formula, rounding: inputs.rounding,
    sex: inputs.sex, age, bodyWeightKilograms: bodyWeight === null ? null : toKilograms(bodyWeight, unit, config),
  };
}

function calculateEstimate(validated, config) {
  const effectiveRepetitions = validated.repetitions + validated.reserve;
  const completedSingle = validated.repetitions === 1 && validated.reserve === 0;
  const estimates = config.formulas.map(formula => ({
    id: formula.id,
    label: formula.label,
    weightKilograms: completedSingle ? validated.weightKilograms : validated.weightKilograms * formula.factor(effectiveRepetitions),
  }));
  if (!estimates.length || estimates.some(item => !Number.isFinite(item.weightKilograms) || item.weightKilograms <= 0)) {
    throw new Error('The formula settings could not produce a valid estimate.');
  }
  const values = estimates.map(item => item.weightKilograms);
  const selected = validated.formula === 'consensus'
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : estimates.find(item => item.id === validated.formula).weightKilograms;
  return {
    validated, estimates, selected, effectiveRepetitions, completedSingle,
    minimum: Math.min(...values), maximum: Math.max(...values),
  };
}

function calculateRepTarget(estimate, repetitions, config) {
  if (repetitions === 1) return estimate.selected;
  const formulas = estimate.validated.formula === 'consensus'
    ? config.formulas
    : config.formulas.filter(item => item.id === estimate.validated.formula);
  const averageFactor = formulas.reduce((sum, item) => sum + item.factor(repetitions), 0) / formulas.length;
  if (!Number.isFinite(averageFactor) || averageFactor <= 0) throw new Error('The selected formula cannot calculate this rep target.');
  return estimate.selected / averageFactor;
}

function roundWeight(weightKilograms, unit, rounding, config) {
  const raw = fromKilograms(weightKilograms, unit, config);
  const increment = rounding === 'none' ? 0 : config.rounding[rounding][unit];
  return increment ? Math.round((raw + Number.EPSILON) / increment) * increment : Number(raw.toFixed(2));
}

function trainingZone(percentage, config) {
  const zone = config.trainingZones.find(item => percentage <= item.through);
  if (!zone) throw new Error('No training zone is configured for this percentage.');
  return zone;
}

function calculatePlateLoad(target, bar, plateConfig) {
  if (target < bar) return { plates: [], actual: bar, note: 'The target is lighter than the selected bar.' };
  const { denominations, quantum } = plateConfig;
  const desiredPerSide = (target - bar) / 2;
  const units = denominations.map(value => Math.round(value / quantum));
  const maximum = Math.ceil(desiredPerSide / quantum) + Math.max(...units);
  const counts = Array(maximum + 1).fill(Infinity);
  const previousPlate = Array(maximum + 1).fill(-1);
  counts[0] = 0;
  for (let total = 1; total <= maximum; total += 1) {
    for (let index = 0; index < units.length; index += 1) {
      if (total >= units[index] && counts[total - units[index]] + 1 < counts[total]) {
        counts[total] = counts[total - units[index]] + 1;
        previousPlate[total] = index;
      }
    }
  }
  let best = 0;
  for (let total = 1; total <= maximum; total += 1) {
    if (!Number.isFinite(counts[total])) continue;
    const difference = Math.abs(total * quantum - desiredPerSide);
    const bestDifference = Math.abs(best * quantum - desiredPerSide);
    if (difference < bestDifference - 1e-8 || (Math.abs(difference - bestDifference) < 1e-8 && counts[total] < counts[best])) best = total;
  }
  const plates = [];
  for (let remaining = best; remaining > 0;) {
    const index = previousPlate[remaining];
    if (index < 0) throw new Error('The plate-loading calculation failed.');
    plates.push(denominations[index]);
    remaining -= units[index];
  }
  plates.sort((left, right) => right - left);
  const actual = bar + 2 * best * quantum;
  const difference = Math.abs(actual - target);
  return { plates, actual, note: difference > 0.001 ? `Closest loadable weight differs by ${displayNumber(difference)}.` : '' };
}

function OneRepMaxCalculator({ config = DEFAULT_CONFIG }) {
  const [inputs, setInputs] = useState(INITIAL_INPUTS);
  const [unit, setUnit] = useState(config.defaultUnit);
  const [target, setTarget] = useState({ type: 'percentage', value: config.defaultTargetPercentage });
  const [barWeight, setBarWeight] = useState(config.plates[config.defaultUnit].bars[0]);
  const [history, setHistory] = useState([]);

  const calculation = useMemo(() => {
    try {
      return { result: calculateEstimate(validateInputs(inputs, unit, config), config), error: '' };
    } catch (error) {
      return { result: null, error: error.message };
    }
  }, [inputs, unit, config]);

  const result = calculation.result;

  const percentageRows = useMemo(() => result ? [
    ...Array(Math.floor((config.percentage.maximum - config.percentage.minimum) / config.percentage.step) + 1).keys(),
  ].map(index => config.percentage.minimum + index * config.percentage.step) : [], [result, config]);

  const repRows = useMemo(() => result ? [...Array(config.repMaxMaximum).keys()].map(index => {
    const repetitions = index + 1;
    return { repetitions, weightKilograms: calculateRepTarget(result, repetitions, config) };
  }) : [], [result, config]);

  const selectedTarget = useMemo(() => {
    if (!result) return null;
    const weightKilograms = target.type === 'rep'
      ? calculateRepTarget(result, target.value, config)
      : result.selected * target.value / 100;
    return {
      label: target.type === 'rep' ? `${target.value}RM target` : `${target.value}% training target`,
      weight: roundWeight(weightKilograms, unit, inputs.rounding, config),
    };
  }, [result, target, unit, inputs.rounding, config]);

  const plateLoad = useMemo(() => selectedTarget
    ? calculatePlateLoad(selectedTarget.weight, barWeight, config.plates[unit])
    : null, [selectedTarget, barWeight, unit, config]);

  const warmup = useMemo(() => {
    if (!selectedTarget || !plateLoad) return [];
    const steps = [
      { label: 'Empty bar', weight: barWeight, repetitions: '8-10' },
      ...config.warmup.map(step => ({
        label: `${Math.round(step.fraction * 100)}% of target`,
        weight: calculatePlateLoad(selectedTarget.weight * step.fraction, barWeight, config.plates[unit]).actual,
        repetitions: String(step.repetitions),
      })),
      { label: 'Working set', weight: plateLoad.actual, repetitions: target.type === 'rep' ? String(target.value) : trainingZone(target.value, config).repetitions },
    ];
    return steps.filter((step, index) => index === 0 || index === steps.length - 1 || (step.weight > barWeight && step.weight < plateLoad.actual && step.weight !== steps[index - 1].weight));
  }, [selectedTarget, plateLoad, barWeight, config, target, unit]);

  function updateInput(field, value) {
    setInputs(previous => ({ ...previous, [field]: value }));
  }

  function changeUnit(nextUnit) {
    if (nextUnit === unit) return;
    const converted = value => {
      if (String(value).trim() === '') return '';
      const number = Number(value);
      if (!Number.isFinite(number)) return value;
      const kilograms = toKilograms(number, unit, config);
      return String(Number(fromKilograms(kilograms, nextUnit, config).toFixed(2)));
    };
    setInputs(previous => ({ ...previous, weight: converted(previous.weight), bodyWeight: converted(previous.bodyWeight) }));
    setUnit(nextUnit);
    setBarWeight(config.plates[nextUnit].bars[0]);
  }

  function saveCalculation(event) {
    event.preventDefault();
    if (!result) return;
    const entry = {
      date: new Date().toLocaleDateString(),
      unit,
      inputs: { ...inputs },
      selected: result.selected,
    };
    setHistory(previous => [entry, ...previous].slice(0, config.historyLimit));
  }

  function restoreHistory(entry) {
    setInputs(entry.inputs);
    setUnit(entry.unit);
    setBarWeight(config.plates[entry.unit].bars[0]);
    setTarget({ type: 'percentage', value: config.defaultTargetPercentage });
  }

  function clearHistory() {
    setHistory([]);
  }

  const warnings = result ? [
    ...(result.validated.repetitions > 10 ? ['More than 10 completed reps: endurance and fatigue increase prediction uncertainty. A 3-8 rep set is generally more useful.'] : []),
    ...(result.effectiveRepetitions > config.maximumReps ? [`Estimated failure reps exceed ${config.maximumReps}. This extrapolation has high variance.`] : []),
  ] : [];

  const selectedFormulaLabel = result?.validated.formula === 'consensus'
    ? 'Consensus'
    : config.formulas.find(item => item.id === result?.validated.formula)?.label;

  const effortValues = inputs.effortMode === 'rpe'
    ? [...Array(Math.round((config.effort.maximumRpe - config.effort.minimumRpe) / config.effort.rpeStep) + 1).keys()].map(index => config.effort.minimumRpe + index * config.effort.rpeStep)
    : [...Array(config.effort.maximumReserve + 1).keys()];

  return (
    <div className="oneRepMaxCalculator">
      <div className="orm-hero">
        <div>
          <p className="orm-eyebrow">Pure Health Nexus / Strength tools</p>
          <h1>Know your strength.<br />Own your next rep.</h1>
          <p className="orm-hero-description">Estimate your one-rep max, build a practical loading plan, and turn one working set into your next training session.</p>
          <span className="ai-promo-inline">
            <strong>{config.promotion.heading}</strong>{' '}
            {config.promotion.description}{' '}
            <a href={config.promotion.href} className="ai-promo-link">{config.promotion.linkText}</a>.
          </span>
        </div>
        <div className="orm-hero-art" aria-hidden="true"><span className="orm-hero-bar" /><span className="orm-art-caption">BUILT FOR THE WORK</span></div>
      </div>
      <div className="orm-layout">
        <form className="orm-panel orm-input-panel" onSubmit={saveCalculation} noValidate>
          <div className="orm-panel-heading"><div><p className="orm-eyebrow">01 / Your set</p><h2>Set details</h2><p>Enter a recent, technically sound set.</p></div><span className="orm-step">01</span></div>
          <label className="orm-field">
            <span className="orm-field-label">Exercise</span>
            <select value={inputs.lift} onChange={event => updateInput('lift', event.target.value)}>
              {config.lifts.map(lift => <option key={lift.id} value={lift.id}>{lift.label}</option>)}
            </select>
          </label>
          <div className="orm-field-row">
            <label className="orm-field">
              <span className="orm-field-label">Weight lifted</span>
              <input
                type="number"
                min="0.01"
                step="any"
                inputMode="decimal"
                placeholder="e.g. 100"
                value={inputs.weight}
                onChange={event => updateInput('weight', event.target.value)}
              />
            </label>
            <label className="orm-field">
              <span className="orm-field-label">Reps completed</span>
              <input
                type="number"
                min="1"
                max={config.maximumReps}
                step="1"
                inputMode="numeric"
                placeholder="e.g. 5"
                value={inputs.repetitions}
                onChange={event => updateInput('repetitions', event.target.value)}
              />
            </label>
          </div>
          <div className="orm-field">
            <span className="orm-field-label">Units</span>
            <div className="orm-segmented" role="group" aria-label="Weight units">
              {Object.keys(config.plates).map(option => (
                <button
                  type="button"
                  key={option}
                  aria-pressed={unit === option}
                  onClick={() => changeUnit(option)}
                >
                  {option === 'kg' ? 'Kilograms' : 'Pounds'}
                </button>
              ))}
            </div>
          </div>
          <div className="orm-field-row">
            <label className="orm-field">
              <span className="orm-field-label">Effort scale</span>
              <select value={inputs.effortMode} onChange={event => updateInput('effortMode', event.target.value)}>
                <option value="rpe">RPE</option>
                <option value="rir">RIR</option>
              </select>
            </label>
            <label className="orm-field">
              <span className="orm-field-label">Effort <span title="RPE 10 / 0 RIR: no reps left; RPE 9 / 1 RIR: one; RPE 8 / 2 RIR: two; RPE 7 / 3 RIR: three.">ⓘ</span></span>
              <select
                value={inputs.effortMode === 'rpe' ? config.effort.maximumRpe - inputs.reserve : inputs.reserve}
                onChange={event => updateInput('reserve', inputs.effortMode === 'rpe' ? config.effort.maximumRpe - Number(event.target.value) : Number(event.target.value))}
              >
                {effortValues.map(value => (
                  <option key={value} value={value}>
                    {inputs.effortMode === 'rpe' ? `RPE ${value.toFixed(1)}` : `${value} RIR`}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="orm-field">
            <span className="orm-field-label">Calculation method</span>
            <select value={inputs.formula} onChange={event => updateInput('formula', event.target.value)}>
              <option value="consensus">Consensus average</option>
              {config.formulas.map(formula => <option key={formula.id} value={formula.id}>{formula.label}</option>)}
            </select>
          </label>
          <label className="orm-field">
            <span className="orm-field-label">Weight rounding</span>
            <select value={inputs.rounding} onChange={event => updateInput('rounding', event.target.value)}>
              <option value="plate">Plate-friendly · {config.rounding.plate.kg} kg / {config.rounding.plate.lb} lb</option>
              <option value="micro">Micro-load · {config.rounding.micro.kg} kg / {config.rounding.micro.lb} lb</option>
              <option value="none">No rounding</option>
            </select>
          </label>
          <div className="orm-field-row">
            <label className="orm-field">
              <span className="orm-field-label">Sex category</span>
              <select value={inputs.sex} onChange={event => updateInput('sex', event.target.value)}>
                <option value="">Select</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </label>
            <label className="orm-field">
              <span className="orm-field-label">Age</span>
              <input
                type="number"
                min={config.minimumAge}
                max={config.maximumAge}
                step="1"
                inputMode="numeric"
                placeholder="Optional"
                value={inputs.age}
                onChange={event => updateInput('age', event.target.value)}
              />
            </label>
          </div>
          <label className="orm-field">
            <span className="orm-field-label">Body weight ({unit})</span>
            <input
              type="number"
              min="1"
              step="any"
              inputMode="decimal"
              placeholder="Optional"
              value={inputs.bodyWeight}
              onChange={event => updateInput('bodyWeight', event.target.value)}
            />
          </label>
          <button className="orm-primary" type="submit" disabled={!result}>Save this calculation →</button>
          <p className="orm-hint">Save up to {config.historyLimit} calculations for this visit. Profile fields are optional.</p>
        </form>

        <div className="orm-results" aria-live="polite">
          <section className="orm-panel orm-result-hero" aria-labelledby="orm-estimate-title">
            <div className="orm-result-top">
              <div>
                <p className="orm-eyebrow">02 / Your estimate</p>
                <h2 id="orm-estimate-title">Estimated one-rep max</h2>
              </div>
              <span className="orm-badge">
                {result ? (warnings.length ? 'Low confidence' : result.completedSingle ? 'Completed single' : 'Estimated 1RM') : 'Awaiting input'}
              </span>
            </div>
            {result ? (
              <React.Fragment>
                <div className="orm-metric">{displayNumber(fromKilograms(result.selected, unit, config))} <span>{unit}</span></div>
                <p className="orm-spread">{result.completedSingle ? 'Actual completed single · no formula extrapolation' : `Formula range: ${weightText(result.minimum, unit, config)} - ${weightText(result.maximum, unit, config)} · half-spread ±${displayNumber(fromKilograms((result.maximum - result.minimum) / 2, unit, config))} ${unit}`}</p>
                <div className="orm-stats">
                  <div><small>Effective reps</small><strong>{displayNumber(result.effectiveRepetitions)}</strong></div>
                  <div><small>Set effort</small><strong>RPE {displayNumber(config.effort.maximumRpe - result.validated.reserve)}</strong></div>
                  <div><small>Method</small><strong>{selectedFormulaLabel}</strong></div>
                </div>
                {warnings.length > 0 && <div className="orm-notice" role="status">{warnings.join(' ')}</div>}
              </React.Fragment>
            ) : (
              <p className="orm-empty">{calculation.error}</p>
            )}
          </section>

          {result && (
            <React.Fragment>
              <section className="orm-panel orm-module" aria-labelledby="orm-formula-title">
                <div className="orm-module-heading">
                  <p className="orm-eyebrow">The calculation</p>
                  <h2 id="orm-formula-title">{config.formulas.length} perspectives. One stronger plan.</h2>
                  <p>Each estimate uses completed reps plus reps in reserve.</p>
                </div>
                <div className="orm-formula-grid">
                  {result.estimates.map(item => (
                    <div className="orm-formula-card" key={item.id}>
                      <small>{item.label}</small>
                      <strong>{weightText(item.weightKilograms, unit, config)}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="orm-panel orm-module" aria-labelledby="orm-zones-title">
                <div className="orm-module-heading">
                  <p className="orm-eyebrow">03 / Training zones</p>
                  <h2 id="orm-zones-title">Your percentage playbook</h2>
                  <p>Choose a row to plan the warm-up and plates.</p>
                </div>
                <div className="orm-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Intensity</th>
                        <th>Target weight</th>
                        <th>Rep guide</th>
                        <th>Training focus</th>
                        <th><span className="orm-sr-only">Action</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {percentageRows.map(percentage => {
                        const zone = trainingZone(percentage, config);
                        return (
                          <tr key={percentage}>
                            <td><strong>{percentage}%</strong></td>
                            <td>{displayNumber(roundWeight(result.selected * percentage / 100, unit, inputs.rounding, config))} {unit}</td>
                            <td>{zone.repetitions} reps</td>
                            <td><span className={`orm-zone orm-${zone.className}`}>{zone.name}</span></td>
                            <td><button type="button" className="orm-link-button" onClick={() => setTarget({ type: 'percentage', value: percentage })}>Plan set</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="orm-hint">Rep ranges are programming guides, not guaranteed reps to failure.</p>
              </section>

              <section className="orm-panel orm-module" aria-labelledby="orm-reps-title">
                <div className="orm-module-heading">
                  <p className="orm-eyebrow">04 / Rep targets</p>
                  <h2 id="orm-reps-title">Find your next rep max</h2>
                  <p>Choose a card to plan your warm-up.</p>
                </div>
                <div className="orm-rep-grid">
                  {repRows.map(row => (
                    <button
                      key={row.repetitions}
                      type="button"
                      className={`orm-rep-card${target.type === 'rep' && target.value === row.repetitions ? ' orm-active' : ''}`}
                      onClick={() => setTarget({ type: 'rep', value: row.repetitions })}
                      aria-label={`Plan ${row.repetitions} rep maximum at ${displayNumber(roundWeight(row.weightKilograms, unit, inputs.rounding, config))} ${unit}`}
                    >
                      <small>{row.repetitions}RM · {Math.round(row.weightKilograms / result.selected * 100)}%</small>
                      <strong>{displayNumber(roundWeight(row.weightKilograms, unit, inputs.rounding, config))} {unit}</strong>
                      <em>Plan warm-up →</em>
                    </button>
                  ))}
                </div>
              </section>

              <div className="orm-split">
                <section className="orm-panel orm-module" aria-labelledby="orm-plates-title">
                  <div className="orm-module-heading orm-heading-inline">
                    <div>
                      <p className="orm-eyebrow">05 / Load the bar</p>
                      <h2 id="orm-plates-title">Plate visualizer</h2>
                      <p>{selectedTarget.label}: {displayNumber(selectedTarget.weight)} {unit}</p>
                    </div>
                    <select
                      className="orm-bar-select"
                      aria-label="Bar weight"
                      value={barWeight}
                      onChange={event => setBarWeight(Number(event.target.value))}
                    >
                      {config.plates[unit].bars.map(weight => (
                        <option key={weight} value={weight}>{weight} {unit} bar</option>
                      ))}
                    </select>
                  </div>
                  <div
                    className="orm-plate-stage"
                    role="img"
                    aria-label={`${displayNumber(plateLoad.actual)} ${unit}: ${barWeight} ${unit} bar, ${plateLoad.plates.map(value => `${value} ${unit}`).join(', ') || 'no plates'} per side`}
                  >
                    {plateLoad.plates.slice().reverse().map((plate, index) => (
                      <span
                        className="orm-plate orm-plate-flip"
                        key={`left-${index}`}
                        style={{
                          '--plate-height': `${Math.max(28, 77 - config.plates[unit].denominations.indexOf(plate) * 6)}px`,
                          '--plate-width': `${Math.max(8, 22 - config.plates[unit].denominations.indexOf(plate))}px`,
                          '--plate-color': config.plateColors[config.plates[unit].denominations.indexOf(plate) % config.plateColors.length],
                        }}
                      >
                        {plate}
                      </span>
                    ))}
                    <span className="orm-bar-shaft" />
                    {plateLoad.plates.map((plate, index) => (
                      <span
                        className="orm-plate"
                        key={`right-${index}`}
                        style={{
                          '--plate-height': `${Math.max(28, 77 - config.plates[unit].denominations.indexOf(plate) * 6)}px`,
                          '--plate-width': `${Math.max(8, 22 - config.plates[unit].denominations.indexOf(plate))}px`,
                          '--plate-color': config.plateColors[config.plates[unit].denominations.indexOf(plate) % config.plateColors.length],
                        }}
                      >
                        {plate}
                      </span>
                    ))}
                  </div>
                  <p className="orm-plate-list">
                    {barWeight} {unit} bar + {plateLoad.plates.length ? `${plateLoad.plates.map(displayNumber).join(' + ')} ${unit} per side` : 'no plates'} = {displayNumber(plateLoad.actual)} {unit}. {plateLoad.note}
                  </p>
                </section>

                <section className="orm-panel orm-module" aria-labelledby="orm-warmup-title">
                  <div className="orm-module-heading">
                    <p className="orm-eyebrow">06 / Prepare</p>
                    <h2 id="orm-warmup-title">Warm-up ramp</h2>
                    <p>Progressive sets toward your working weight.</p>
                  </div>
                  <div className="orm-ramp">
                    {warmup.map((step, index) => (
                      <div className="orm-ramp-row" key={`${step.label}-${index}`}>
                        <b>{String(index + 1).padStart(2, '0')}</b>
                        <span>{step.label} · {step.repetitions} reps</span>
                        <strong>{displayNumber(step.weight)} {unit}</strong>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </React.Fragment>
          )}

          {(result || history.length > 0) && (
            <div className="orm-split">
              <section className="orm-panel orm-module" aria-labelledby="orm-history-title">
                <div className="orm-module-heading orm-heading-inline">
                  <div>
                    <p className="orm-eyebrow">07 / Recent work</p>
                    <h2 id="orm-history-title">Your last {config.historyLimit} sets</h2>
                    <p>Available until you leave or refresh this page.</p>
                  </div>
                  <button type="button" className="orm-secondary" onClick={clearHistory}>Clear</button>
                </div>
                <div className="orm-history">
                  {history.length ? history.map((entry, index) => (
                    <div className="orm-history-item" key={`${entry.date}-${index}`}>
                      <button type="button" onClick={() => restoreHistory(entry)}>
                        <small>{entry.date} · {config.lifts.find(lift => lift.id === entry.inputs.lift)?.label} · {entry.inputs.repetitions} reps</small>
                        {displayNumber(Number(entry.inputs.weight))} {entry.unit} @ RPE {displayNumber(config.effort.maximumRpe - entry.inputs.reserve)}
                      </button>
                      <strong>{weightText(entry.selected, entry.unit, config)}</strong>
                    </div>
                  )) : <p className="orm-benchmark-note">No saved sets yet.</p>}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
      <p className="orm-footnote">Estimates are planning tools, not tested maximums. Higher reps and submaximal effort increase uncertainty.</p>
    </div>
  );
}

export default OneRepMaxCalculator;